/**
 * Application Review Component
 * 
 * AI-powered pre-submission validation for immigration applications.
 * Checks documents, forms, and data consistency before submission.
 */

import React, { useState, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%);
  color: #fff;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid rgba(100, 150, 200, 0.15);
  background: linear-gradient(90deg, rgba(25, 30, 45, 0.98) 0%, rgba(35, 45, 65, 0.95) 100%);
`;

const Title = styled.h2`
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: rgba(200, 220, 255, 0.95);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: rgba(180, 195, 220, 0.6);
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(100, 150, 200, 0.1);
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(100, 150, 200, 0.2);
    border-radius: 3px;
  }
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(100, 150, 200, 0.2);
    border-radius: 3px;
  }
`;

const Section = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid rgba(100, 150, 200, 0.1);
  animation: ${fadeIn} 0.3s ease;
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: rgba(200, 220, 255, 0.9);
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    width: 18px;
    height: 18px;
    color: rgba(100, 160, 230, 0.8);
  }
`;

// Category Filter
const CategoryFilter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const CategoryButton = styled.button`
  padding: 6px 14px;
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, rgba(60, 120, 200, 0.4) 0%, rgba(80, 140, 220, 0.3) 100%)'
    : 'rgba(30, 40, 60, 0.4)'};
  border: 1px solid ${props => props.$active 
    ? 'rgba(100, 160, 230, 0.6)' 
    : 'rgba(100, 150, 200, 0.2)'};
  border-radius: 20px;
  color: ${props => props.$active ? 'rgba(150, 200, 255, 0.95)' : 'rgba(180, 195, 220, 0.7)'};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(60, 120, 200, 0.3) 0%, rgba(80, 140, 220, 0.2) 100%);
    border-color: rgba(100, 160, 230, 0.4);
    color: rgba(150, 200, 255, 0.9);
  }
`;

// Application Type Dropdown
const ApplicationTypeSelect = styled.select`
  width: 100%;
  padding: 14px 16px;
  background: rgba(20, 25, 40, 0.8);
  border: 1px solid rgba(100, 150, 200, 0.25);
  border-radius: 10px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  
  &:focus {
    outline: none;
    border-color: rgba(100, 160, 230, 0.6);
    box-shadow: 0 0 0 3px rgba(100, 160, 230, 0.15);
  }
  
  &:hover {
    border-color: rgba(100, 160, 230, 0.4);
  }
  
  option {
    background: #1a1f2e;
    color: #fff;
    padding: 12px;
  }
  
  optgroup {
    background: #141a23;
    color: rgba(100, 160, 230, 0.9);
    font-weight: 600;
    font-style: normal;
  }
`;

const SelectedTypeInfo = styled.div`
  margin-top: 12px;
  padding: 12px 16px;
  background: rgba(60, 120, 200, 0.1);
  border: 1px solid rgba(100, 160, 230, 0.2);
  border-radius: 8px;
  
  h4 {
    margin: 0 0 4px 0;
    font-size: 14px;
    font-weight: 500;
    color: rgba(150, 200, 255, 0.95);
  }
  
  p {
    margin: 0;
    font-size: 12px;
    color: rgba(180, 195, 220, 0.6);
  }
`;

// Document Checklist
const ChecklistContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${props => props.$uploaded 
    ? 'rgba(16, 185, 129, 0.08)' 
    : 'rgba(30, 40, 60, 0.4)'};
  border: 1px solid ${props => props.$uploaded 
    ? 'rgba(16, 185, 129, 0.3)' 
    : 'rgba(100, 150, 200, 0.15)'};
  border-radius: 8px;
  transition: all 0.2s ease;
`;

const Checkbox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 2px solid ${props => props.$checked 
    ? 'rgba(16, 185, 129, 0.8)' 
    : 'rgba(100, 150, 200, 0.3)'};
  background: ${props => props.$checked 
    ? 'rgba(16, 185, 129, 0.2)' 
    : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 12px;
    height: 12px;
    color: #10b981;
    opacity: ${props => props.$checked ? 1 : 0};
  }
`;

const ChecklistItemContent = styled.div`
  flex: 1;
  
  h5 {
    margin: 0 0 2px 0;
    font-size: 13px;
    font-weight: 500;
    color: rgba(200, 220, 255, 0.9);
  }
  
  span {
    font-size: 11px;
    color: rgba(180, 195, 220, 0.5);
  }
`;

const UploadButton = styled.button`
  padding: 6px 12px;
  background: rgba(60, 120, 200, 0.15);
  border: 1px solid rgba(100, 160, 230, 0.3);
  border-radius: 6px;
  color: rgba(150, 200, 255, 0.9);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(60, 120, 200, 0.25);
  }
`;

const FileName = styled.span`
  font-size: 12px;
  color: rgba(16, 185, 129, 0.9);
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Application Details Form
const FormGroup = styled.div`
  margin-bottom: 16px;
  
  label {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 500;
    color: rgba(180, 195, 220, 0.7);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  background: rgba(20, 25, 40, 0.6);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(100, 160, 230, 0.5);
    background: rgba(20, 25, 40, 0.8);
  }
  
  &::placeholder {
    color: rgba(180, 195, 220, 0.4);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  background: rgba(20, 25, 40, 0.6);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(100, 160, 230, 0.5);
    background: rgba(20, 25, 40, 0.8);
  }
  
  &::placeholder {
    color: rgba(180, 195, 220, 0.4);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 14px;
  background: rgba(20, 25, 40, 0.6);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: rgba(100, 160, 230, 0.5);
  }
  
  option {
    background: #1a1f2e;
  }
`;

// Validate Button
const ValidateButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, rgba(60, 120, 200, 0.9) 0%, rgba(80, 140, 220, 0.95) 100%);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(70, 130, 210, 0.95) 0%, rgba(90, 150, 230, 1) 100%);
    box-shadow: 0 4px 20px rgba(60, 120, 200, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

// Results Section
const ResultsContainer = styled.div`
  padding: 24px;
  animation: ${slideIn} 0.4s ease;
`;

const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const ScoreCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => {
    if (props.$score >= 80) return 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)';
    if (props.$score >= 60) return 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)';
    return 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%)';
  }};
  border: 3px solid ${props => {
    if (props.$score >= 80) return 'rgba(16, 185, 129, 0.6)';
    if (props.$score >= 60) return 'rgba(245, 158, 11, 0.6)';
    return 'rgba(239, 68, 68, 0.6)';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  
  span:first-child {
    font-size: 24px;
    font-weight: 700;
    color: ${props => {
      if (props.$score >= 80) return '#10b981';
      if (props.$score >= 60) return '#f59e0b';
      return '#ef4444';
    }};
  }
  
  span:last-child {
    font-size: 11px;
    color: rgba(180, 195, 220, 0.6);
  }
`;

const ResultsSummary = styled.div`
  flex: 1;
  
  h3 {
    margin: 0 0 4px 0;
    font-size: 18px;
    font-weight: 600;
    color: ${props => {
      if (props.$score >= 80) return '#10b981';
      if (props.$score >= 60) return '#f59e0b';
      return '#ef4444';
    }};
  }
  
  p {
    margin: 0;
    font-size: 13px;
    color: rgba(180, 195, 220, 0.7);
  }
`;

const ResultsSection = styled.div`
  margin-bottom: 20px;
  
  h4 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
    color: rgba(200, 220, 255, 0.9);
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const ResultItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: ${props => {
    if (props.$type === 'success') return 'rgba(16, 185, 129, 0.08)';
    if (props.$type === 'warning') return 'rgba(245, 158, 11, 0.08)';
    return 'rgba(239, 68, 68, 0.08)';
  }};
  border: 1px solid ${props => {
    if (props.$type === 'success') return 'rgba(16, 185, 129, 0.2)';
    if (props.$type === 'warning') return 'rgba(245, 158, 11, 0.2)';
    return 'rgba(239, 68, 68, 0.2)';
  }};
  border-radius: 8px;
  margin-bottom: 8px;
  
  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 2px;
    color: ${props => {
      if (props.$type === 'success') return '#10b981';
      if (props.$type === 'warning') return '#f59e0b';
      return '#ef4444';
    }};
  }
`;

const ResultItemContent = styled.div`
  flex: 1;
  
  h5 {
    margin: 0 0 4px 0;
    font-size: 13px;
    font-weight: 500;
    color: rgba(200, 220, 255, 0.9);
  }
  
  p {
    margin: 0;
    font-size: 12px;
    color: rgba(180, 195, 220, 0.6);
    line-height: 1.5;
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  
  svg {
    width: 48px;
    height: 48px;
    color: rgba(100, 160, 230, 0.8);
    animation: ${spin} 1s linear infinite;
    margin-bottom: 16px;
  }
  
  h4 {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 500;
    color: rgba(200, 220, 255, 0.9);
  }
  
  p {
    margin: 0;
    font-size: 13px;
    color: rgba(180, 195, 220, 0.6);
    animation: ${pulse} 1.5s ease infinite;
  }
`;

const EmptyResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  
  svg {
    width: 64px;
    height: 64px;
    color: rgba(100, 150, 200, 0.3);
    margin-bottom: 16px;
  }
  
  h4 {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 500;
    color: rgba(200, 220, 255, 0.7);
  }
  
  p {
    margin: 0;
    font-size: 13px;
    color: rgba(180, 195, 220, 0.5);
    max-width: 300px;
  }
`;

// ============================================
// APPLICATION TYPES & REQUIRED DOCS
// ============================================

// ============================================
// APPLICATION TYPES - COMPREHENSIVE LIST
// ============================================

const APPLICATION_TYPES = [
  // ========== EMPLOYMENT-BASED ==========
  { 
    id: 'work_permit', 
    name: 'Work Permit', 
    category: 'Employment',
    description: 'General employment authorization',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid for at least 6 months' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent, within 6 months' },
      { id: 'job_offer', name: 'Job Offer Letter', description: 'From sponsoring employer' },
      { id: 'education', name: 'Education Certificates', description: 'Diplomas, degrees' },
      { id: 'cv', name: 'CV / Resume', description: 'Updated work history' },
      { id: 'police_clearance', name: 'Police Clearance', description: 'Criminal background check' },
      { id: 'medical_exam', name: 'Medical Examination', description: 'Health certificate' },
    ]
  },
  { 
    id: 'skilled_worker', 
    name: 'Skilled Worker Visa', 
    category: 'Employment',
    description: 'Points-based skilled worker entry',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'job_offer', name: 'Job Offer / CoS', description: 'Certificate of Sponsorship' },
      { id: 'education', name: 'Education Certificates', description: 'Degree, diploma with translations' },
      { id: 'language_test', name: 'English Language Test', description: 'IELTS, PTE, etc.' },
      { id: 'cv', name: 'CV / Resume', description: 'Detailed work history' },
      { id: 'reference_letters', name: 'Reference Letters', description: 'From previous employers' },
      { id: 'salary_proof', name: 'Salary Evidence', description: 'Meets minimum threshold' },
    ]
  },
  { 
    id: 'intra_company_transfer', 
    name: 'Intra-Company Transfer', 
    category: 'Employment',
    description: 'Transfer within multinational company',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'employment_contract', name: 'Employment Contract', description: 'Current and proposed' },
      { id: 'company_letter', name: 'Company Transfer Letter', description: 'From HR department' },
      { id: 'org_chart', name: 'Organization Chart', description: 'Showing position' },
      { id: 'payslips', name: 'Payslips', description: 'Last 3-6 months' },
      { id: 'cv', name: 'CV / Resume', description: 'Work history' },
    ]
  },
  { 
    id: 'temporary_worker', 
    name: 'Temporary Worker Visa', 
    category: 'Employment',
    description: 'Short-term work authorization',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'job_offer', name: 'Job Offer Letter', description: 'Temporary position details' },
      { id: 'employer_docs', name: 'Employer Documents', description: 'Company registration' },
      { id: 'contract', name: 'Employment Contract', description: 'Terms and duration' },
    ]
  },
  { 
    id: 'seasonal_worker', 
    name: 'Seasonal Worker Visa', 
    category: 'Employment',
    description: 'Agriculture/hospitality seasonal work',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'sponsor_cert', name: 'Sponsor Certificate', description: 'From licensed sponsor' },
      { id: 'job_details', name: 'Job Details', description: 'Role, dates, location' },
    ]
  },

  // ========== EDUCATION-BASED ==========
  { 
    id: 'student_visa', 
    name: 'Student Visa', 
    category: 'Education',
    description: 'Full-time study program',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid for program duration' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent, within 6 months' },
      { id: 'cas', name: 'CAS / I-20 / CoE', description: 'Confirmation of Acceptance' },
      { id: 'acceptance_letter', name: 'Acceptance Letter', description: 'From educational institution' },
      { id: 'transcripts', name: 'Academic Transcripts', description: 'Previous education records' },
      { id: 'financial_proof', name: 'Financial Proof', description: 'Tuition + living costs' },
      { id: 'language_test', name: 'Language Test Results', description: 'IELTS, TOEFL, etc.' },
      { id: 'tb_test', name: 'TB Test Certificate', description: 'If from listed country' },
    ]
  },
  { 
    id: 'student_dependent', 
    name: 'Student Dependent Visa', 
    category: 'Education',
    description: 'Spouse/children of student',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'relationship_proof', name: 'Relationship Proof', description: 'Marriage/birth certificate' },
      { id: 'main_applicant_docs', name: 'Main Applicant Visa', description: 'Copy of student visa' },
      { id: 'financial_proof', name: 'Financial Proof', description: 'Maintenance funds' },
    ]
  },
  { 
    id: 'exchange_visitor', 
    name: 'Exchange Visitor (J-1)', 
    category: 'Education',
    description: 'Cultural exchange program',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'ds2019', name: 'DS-2019 Form', description: 'From program sponsor' },
      { id: 'sevis_receipt', name: 'SEVIS Fee Receipt', description: 'Payment confirmation' },
      { id: 'program_details', name: 'Program Details', description: 'Itinerary and purpose' },
      { id: 'financial_proof', name: 'Financial Proof', description: 'Funding source' },
    ]
  },

  // ========== FAMILY-BASED ==========
  { 
    id: 'spouse_visa', 
    name: 'Spouse Visa', 
    category: 'Family',
    description: 'Join spouse/partner abroad',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'marriage_cert', name: 'Marriage Certificate', description: 'Registered marriage proof' },
      { id: 'relationship_evidence', name: 'Relationship Evidence', description: 'Photos, communications, etc.' },
      { id: 'sponsor_status', name: 'Sponsor Status Proof', description: 'Passport, visa, citizenship' },
      { id: 'financial_proof', name: 'Financial Requirement', description: 'Income threshold met' },
      { id: 'accommodation', name: 'Accommodation Proof', description: 'Where you will live' },
      { id: 'language_test', name: 'Language Test', description: 'If required' },
    ]
  },
  { 
    id: 'fiance_visa', 
    name: 'Fiancé(e) Visa (K-1)', 
    category: 'Family',
    description: 'Marry citizen within country',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'i129f', name: 'I-129F Approval', description: 'Petition approval notice' },
      { id: 'meeting_proof', name: 'Meeting Proof', description: 'Met in person within 2 years' },
      { id: 'relationship_evidence', name: 'Relationship Evidence', description: 'Photos, communications' },
      { id: 'divorce_death_cert', name: 'Previous Marriage Docs', description: 'If previously married' },
      { id: 'police_clearance', name: 'Police Clearance', description: 'Background check' },
      { id: 'medical_exam', name: 'Medical Examination', description: 'From approved physician' },
    ]
  },
  { 
    id: 'parent_visa', 
    name: 'Parent Visa', 
    category: 'Family',
    description: 'Parents joining children',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'birth_cert', name: 'Birth Certificate', description: 'Proving relationship' },
      { id: 'sponsor_docs', name: 'Sponsor Documents', description: 'Child\'s status proof' },
      { id: 'financial_proof', name: 'Financial Proof', description: 'Sponsorship affidavit' },
      { id: 'medical_exam', name: 'Medical Examination', description: 'Health certificate' },
    ]
  },
  { 
    id: 'child_visa', 
    name: 'Child Visa', 
    category: 'Family',
    description: 'Minor children joining parents',
    requiredDocs: [
      { id: 'passport', name: 'Child Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'birth_cert', name: 'Birth Certificate', description: 'Showing parents' },
      { id: 'parent_consent', name: 'Parental Consent', description: 'If one parent not traveling' },
      { id: 'parent_docs', name: 'Parent Documents', description: 'Visa/status of parent abroad' },
      { id: 'school_records', name: 'School Records', description: 'If school age' },
    ]
  },
  { 
    id: 'family_reunification', 
    name: 'Family Reunification', 
    category: 'Family',
    description: 'General family-based immigration',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'relationship_proof', name: 'Relationship Proof', description: 'Birth/marriage certificates' },
      { id: 'sponsor_docs', name: 'Sponsor Documents', description: 'Status in destination country' },
      { id: 'financial_proof', name: 'Financial Proof', description: 'Ability to support' },
      { id: 'accommodation', name: 'Accommodation Proof', description: 'Housing arrangement' },
    ]
  },

  // ========== BUSINESS & INVESTMENT ==========
  { 
    id: 'business_visa', 
    name: 'Business Visitor Visa', 
    category: 'Business',
    description: 'Short-term business activities',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'invitation_letter', name: 'Invitation Letter', description: 'From business contact' },
      { id: 'company_letter', name: 'Company Letter', description: 'Employer authorization' },
      { id: 'itinerary', name: 'Travel Itinerary', description: 'Meeting schedule' },
      { id: 'financial_proof', name: 'Financial Proof', description: 'Funds for trip' },
    ]
  },
  { 
    id: 'investor_visa', 
    name: 'Investor Visa (E-2/EB-5)', 
    category: 'Business',
    description: 'Substantial investment immigration',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'business_plan', name: 'Business Plan', description: 'Detailed 5-year plan' },
      { id: 'investment_proof', name: 'Investment Proof', description: 'Source of funds, transfers' },
      { id: 'company_docs', name: 'Company Documents', description: 'Registration, articles' },
      { id: 'financial_statements', name: 'Financial Statements', description: 'Personal and business' },
      { id: 'job_creation', name: 'Job Creation Plan', description: 'Employment projections' },
    ]
  },
  { 
    id: 'entrepreneur_visa', 
    name: 'Entrepreneur / Startup Visa', 
    category: 'Business',
    description: 'Innovative business founders',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'endorsement', name: 'Endorsement Letter', description: 'From approved body' },
      { id: 'business_plan', name: 'Business Plan', description: 'Innovation and viability' },
      { id: 'funding_proof', name: 'Funding Proof', description: 'Investment secured' },
      { id: 'financial_proof', name: 'Personal Finances', description: 'Maintenance funds' },
    ]
  },
  { 
    id: 'self_employed', 
    name: 'Self-Employed Visa', 
    category: 'Business',
    description: 'Freelancers and contractors',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'business_registration', name: 'Business Registration', description: 'Self-employment proof' },
      { id: 'contracts', name: 'Client Contracts', description: 'Ongoing work agreements' },
      { id: 'financial_proof', name: 'Financial Proof', description: 'Income evidence' },
      { id: 'tax_returns', name: 'Tax Returns', description: 'Previous years' },
    ]
  },

  // ========== PERMANENT RESIDENCE ==========
  { 
    id: 'permanent_residence', 
    name: 'Permanent Residence', 
    category: 'Settlement',
    description: 'Long-term settlement application',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Current and previous' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'residence_proof', name: 'Residence Proof', description: 'Continuous residence evidence' },
      { id: 'visa_history', name: 'Visa History', description: 'All previous visas' },
      { id: 'language_test', name: 'Language Test', description: 'Life in the UK, etc.' },
      { id: 'life_test', name: 'Knowledge Test', description: 'Civics test certificate' },
      { id: 'financial_proof', name: 'Financial Proof', description: 'Employment, income' },
      { id: 'tax_records', name: 'Tax Records', description: 'Payment history' },
      { id: 'police_clearance', name: 'Police Clearance', description: 'Good character' },
    ]
  },
  { 
    id: 'citizenship', 
    name: 'Citizenship / Naturalization', 
    category: 'Settlement',
    description: 'Becoming a citizen',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Current passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'pr_card', name: 'PR Card / Status', description: 'Current permanent residence' },
      { id: 'residence_proof', name: 'Residence Proof', description: '5+ years typically' },
      { id: 'language_test', name: 'Language Test', description: 'Required level' },
      { id: 'civics_test', name: 'Civics Test', description: 'Citizenship test pass' },
      { id: 'tax_returns', name: 'Tax Returns', description: '3-5 years' },
      { id: 'references', name: 'Character References', description: 'From citizens' },
    ]
  },

  // ========== HUMANITARIAN ==========
  { 
    id: 'asylum', 
    name: 'Asylum Application', 
    category: 'Humanitarian',
    description: 'Protection from persecution',
    requiredDocs: [
      { id: 'passport', name: 'Passport / ID', description: 'If available' },
      { id: 'photo', name: 'Photo', description: 'Identification photo' },
      { id: 'personal_statement', name: 'Personal Statement', description: 'Detailed claim narrative' },
      { id: 'country_evidence', name: 'Country Evidence', description: 'Reports, news articles' },
      { id: 'persecution_proof', name: 'Persecution Evidence', description: 'Threats, injuries, etc.' },
      { id: 'witness_statements', name: 'Witness Statements', description: 'Supporting testimonies' },
      { id: 'medical_evidence', name: 'Medical Evidence', description: 'If applicable' },
    ]
  },
  { 
    id: 'refugee', 
    name: 'Refugee Status', 
    category: 'Humanitarian',
    description: 'UNHCR refugee resettlement',
    requiredDocs: [
      { id: 'unhcr_docs', name: 'UNHCR Documents', description: 'Refugee determination' },
      { id: 'id_docs', name: 'Identity Documents', description: 'Any available ID' },
      { id: 'family_docs', name: 'Family Documents', description: 'Relationship proof' },
      { id: 'medical_records', name: 'Medical Records', description: 'Health information' },
    ]
  },
  { 
    id: 'humanitarian_protection', 
    name: 'Humanitarian Protection', 
    category: 'Humanitarian',
    description: 'Subsidiary protection status',
    requiredDocs: [
      { id: 'passport', name: 'Passport / ID', description: 'If available' },
      { id: 'photo', name: 'Photo', description: 'Identification photo' },
      { id: 'personal_statement', name: 'Personal Statement', description: 'Circumstances description' },
      { id: 'country_evidence', name: 'Country Conditions', description: 'Reports on home country' },
    ]
  },

  // ========== VISITOR & TOURISM ==========
  { 
    id: 'tourist_visa', 
    name: 'Tourist / Visitor Visa', 
    category: 'Visitor',
    description: 'Short-term tourism visit',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid 6+ months' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'itinerary', name: 'Travel Itinerary', description: 'Flights, hotels booked' },
      { id: 'financial_proof', name: 'Financial Proof', description: 'Bank statements' },
      { id: 'employment_proof', name: 'Employment Proof', description: 'Letter from employer' },
      { id: 'ties_proof', name: 'Ties to Home Country', description: 'Property, family, job' },
    ]
  },
  { 
    id: 'medical_visa', 
    name: 'Medical Treatment Visa', 
    category: 'Visitor',
    description: 'Seeking medical treatment',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'medical_letter', name: 'Medical Letter', description: 'From treating physician' },
      { id: 'hospital_letter', name: 'Hospital Letter', description: 'Treatment confirmation' },
      { id: 'treatment_plan', name: 'Treatment Plan', description: 'Procedure details' },
      { id: 'financial_proof', name: 'Financial Proof', description: 'Can afford treatment' },
    ]
  },
  { 
    id: 'transit_visa', 
    name: 'Transit Visa', 
    category: 'Visitor',
    description: 'Passing through country',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'flight_tickets', name: 'Flight Tickets', description: 'Onward journey proof' },
      { id: 'destination_visa', name: 'Destination Visa', description: 'If required' },
    ]
  },

  // ========== SPECIAL CATEGORIES ==========
  { 
    id: 'religious_worker', 
    name: 'Religious Worker Visa', 
    category: 'Special',
    description: 'Ministers, religious roles',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'ordination', name: 'Ordination Certificate', description: 'Religious credentials' },
      { id: 'sponsor_letter', name: 'Sponsor Letter', description: 'From religious organization' },
      { id: 'qualifications', name: 'Qualifications', description: 'Religious training' },
    ]
  },
  { 
    id: 'artist_entertainer', 
    name: 'Artist / Entertainer Visa', 
    category: 'Special',
    description: 'Performers, artists, athletes',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'performance_contract', name: 'Performance Contract', description: 'Event details' },
      { id: 'portfolio', name: 'Portfolio / Press', description: 'Evidence of recognition' },
      { id: 'itinerary', name: 'Event Itinerary', description: 'Tour schedule' },
    ]
  },
  { 
    id: 'journalist', 
    name: 'Journalist / Media Visa', 
    category: 'Special',
    description: 'Media and press coverage',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'media_credentials', name: 'Media Credentials', description: 'Press card' },
      { id: 'employer_letter', name: 'Employer Letter', description: 'Assignment details' },
      { id: 'coverage_plan', name: 'Coverage Plan', description: 'Story/event details' },
    ]
  },
  { 
    id: 'domestic_worker', 
    name: 'Domestic Worker Visa', 
    category: 'Special',
    description: 'Private household staff',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'employment_contract', name: 'Employment Contract', description: 'Terms and conditions' },
      { id: 'employer_docs', name: 'Employer Documents', description: 'Employer\'s status' },
      { id: 'previous_employment', name: 'Previous Employment', description: '12+ months proof' },
    ]
  },
  { 
    id: 'retirement_visa', 
    name: 'Retirement Visa', 
    category: 'Special',
    description: 'Retirees relocating abroad',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'Valid passport' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'pension_proof', name: 'Pension Proof', description: 'Regular income' },
      { id: 'savings_proof', name: 'Savings Proof', description: 'Financial stability' },
      { id: 'health_insurance', name: 'Health Insurance', description: 'Medical coverage' },
      { id: 'accommodation', name: 'Accommodation Proof', description: 'Where you will live' },
    ]
  },

  // ========== EXTENSIONS & STATUS CHANGES ==========
  { 
    id: 'visa_extension', 
    name: 'Visa Extension', 
    category: 'Status Change',
    description: 'Extend current visa',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'With current visa' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'current_visa', name: 'Current Visa Copy', description: 'Existing authorization' },
      { id: 'extension_reason', name: 'Extension Letter', description: 'Reason for extension' },
      { id: 'supporting_docs', name: 'Supporting Documents', description: 'Category-specific' },
      { id: 'financial_proof', name: 'Financial Proof', description: 'Continued maintenance' },
    ]
  },
  { 
    id: 'status_change', 
    name: 'Change of Status', 
    category: 'Status Change',
    description: 'Switch visa category',
    requiredDocs: [
      { id: 'passport', name: 'Passport', description: 'With current visa' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'current_status', name: 'Current Status Proof', description: 'Existing visa/status' },
      { id: 'new_category_docs', name: 'New Category Documents', description: 'Requirements for new visa' },
      { id: 'transition_letter', name: 'Transition Letter', description: 'Explaining change' },
    ]
  },
  { 
    id: 'travel_document', 
    name: 'Travel Document', 
    category: 'Status Change',
    description: 'Refugee travel document / Re-entry permit',
    requiredDocs: [
      { id: 'passport', name: 'Passport / Status Doc', description: 'Current document' },
      { id: 'photo', name: 'Passport Photo', description: 'Recent photo' },
      { id: 'status_proof', name: 'Status Proof', description: 'PR, refugee status' },
      { id: 'travel_plans', name: 'Travel Plans', description: 'Reason for travel' },
    ]
  },

  // ========== APPEALS & MOTIONS ==========
  { 
    id: 'appeal', 
    name: 'Immigration Appeal', 
    category: 'Legal',
    description: 'Appeal visa refusal',
    requiredDocs: [
      { id: 'refusal_letter', name: 'Refusal Letter', description: 'Original decision' },
      { id: 'appeal_grounds', name: 'Appeal Grounds', description: 'Detailed submission' },
      { id: 'additional_evidence', name: 'Additional Evidence', description: 'New supporting docs' },
      { id: 'legal_submissions', name: 'Legal Submissions', description: 'Case law, arguments' },
    ]
  },
  { 
    id: 'administrative_review', 
    name: 'Administrative Review', 
    category: 'Legal',
    description: 'Review of caseworker decision',
    requiredDocs: [
      { id: 'decision_letter', name: 'Decision Letter', description: 'Original decision' },
      { id: 'review_grounds', name: 'Review Grounds', description: 'Error identification' },
      { id: 'supporting_evidence', name: 'Supporting Evidence', description: 'Clarifying documents' },
    ]
  },
];

// ============================================
// COMPONENT
// ============================================

const ApplicationReview = ({ threadId, conversation }) => {
  const authenticatedAxios = useAuthenticatedAxios();
  const fileInputRefs = useRef({});
  
  // State
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [applicationDetails, setApplicationDetails] = useState({
    applicantName: '',
    targetCountry: '',
    programDetails: '',
    additionalNotes: '',
  });
  const [isValidating, setIsValidating] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  
  // New state for existing files and sequential validation
  const [existingFiles, setExistingFiles] = useState([]);
  const [showFileSelector, setShowFileSelector] = useState(null); // docId to show selector for
  const [validationProgress, setValidationProgress] = useState(null); // { current: 1, total: 5, currentFile: 'passport.pdf' }
  const [fileValidationResults, setFileValidationResults] = useState({}); // { docId: { status, score, issues } }
  
  // Form completion status
  const [formCompletionData, setFormCompletionData] = useState(null);
  const [loadingFormStatus, setLoadingFormStatus] = useState(false);

  // Load existing files from conversation
  React.useEffect(() => {
    const loadExistingFiles = async () => {
      if (!threadId) return;
      try {
        // Use the correct endpoint for conversation files
        const response = await authenticatedAxios.get(`/conversation-files/${threadId}`);
        if (response.data?.files) {
          setExistingFiles(response.data.files);
        }
      } catch (error) {
        console.error('Error loading existing files:', error);
      }
    };
    loadExistingFiles();
  }, [threadId, authenticatedAxios]);
  
  // Load form completion status
  React.useEffect(() => {
    const loadFormStatus = async () => {
      if (!threadId) return;
      setLoadingFormStatus(true);
      try {
        const response = await authenticatedAxios.get(`/api/application-review/form-status/${threadId}`);
        if (response.data?.success) {
          setFormCompletionData(response.data);
        }
      } catch (error) {
        console.error('Error loading form status:', error);
      } finally {
        setLoadingFormStatus(false);
      }
    };
    loadFormStatus();
  }, [threadId, authenticatedAxios]);

  // Handle selecting an existing file
  const handleSelectExistingFile = useCallback(async (docId, file) => {
    try {
      let downloadUrl = file.downloadUrl;
      
      // If no download URL, get a signed URL from the server
      if (!downloadUrl && file.fileName) {
        try {
          const urlResponse = await authenticatedAxios.post('/conversation-files/download', {
            fileKey: file.fileName
          });
          downloadUrl = urlResponse.data?.downloadUrl;
        } catch (urlError) {
          console.error('Error getting download URL:', urlError);
        }
      }

      if (!downloadUrl) {
        console.error('No download URL available for file:', file);
        // Still add the file reference even without content
        setUploadedFiles(prev => ({
          ...prev,
          [docId]: {
            file: null,
            name: file.originalName || file.fileName || file.name,
            base64: null,
            type: file.fileType || 'application/pdf',
            isExisting: true,
            fileId: file.id,
            fileName: file.fileName
          }
        }));
        setShowFileSelector(null);
        return;
      }

      // Fetch the file content
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedFiles(prev => ({
          ...prev,
          [docId]: {
            file: blob,
            name: file.originalName || file.fileName || file.name,
            base64: reader.result,
            type: blob.type || file.fileType || 'application/pdf',
            isExisting: true,
            fileId: file.id,
            fileName: file.fileName
          }
        }));
        setShowFileSelector(null);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Error loading existing file:', error);
    }
  }, [authenticatedAxios]);

  // Get unique categories
  const categories = ['All', ...new Set(APPLICATION_TYPES.map(t => t.category))];
  
  // Filter application types by category
  const filteredTypes = selectedCategory === 'All' 
    ? APPLICATION_TYPES 
    : APPLICATION_TYPES.filter(t => t.category === selectedCategory);

  // Get required docs for selected type
  const requiredDocs = selectedType 
    ? APPLICATION_TYPES.find(t => t.id === selectedType)?.requiredDocs || []
    : [];

  // Handle file upload
  const handleFileUpload = useCallback((docId, event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Convert to base64 for API
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedFiles(prev => ({
          ...prev,
          [docId]: {
            file,
            name: file.name,
            base64: reader.result,
            type: file.type,
          }
        }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Handle sequential validation - one file at a time
  const handleValidate = async () => {
    if (!selectedType) return;
    
    setIsValidating(true);
    setValidationResults(null);
    setFileValidationResults({});

    const filesToValidate = Object.entries(uploadedFiles);
    const totalFiles = filesToValidate.length;
    const results = {};
    let totalScore = 0;
    let hasErrors = false;
    let hasWarnings = false;

    try {
      // Validate each file sequentially
      for (let i = 0; i < filesToValidate.length; i++) {
        const [docId, fileData] = filesToValidate[i];
        const docInfo = requiredDocs.find(d => d.id === docId);
        
        // Update progress
        setValidationProgress({
          current: i + 1,
          total: totalFiles,
          currentFile: fileData.name,
          currentDoc: docInfo?.name || docId
        });

        try {
          const response = await authenticatedAxios.post('/api/application-review/validate-single', {
            applicationType: selectedType,
            documentType: docId,
            documentName: docInfo?.name || docId,
            fileName: fileData.name,
            base64: fileData.base64,
            mimeType: fileData.type,
            applicationDetails,
            threadId,
          });

          results[docId] = response.data;
          totalScore += response.data.score || 0;
          
          if (response.data.status === 'error') hasErrors = true;
          if (response.data.status === 'warning') hasWarnings = true;
          
          // Update results in real-time
          setFileValidationResults(prev => ({
            ...prev,
            [docId]: response.data
          }));
        } catch (fileError) {
          console.error(`Error validating ${docId}:`, fileError);
          results[docId] = {
            status: 'error',
            score: 0,
            issues: ['Failed to validate this document'],
            recommendations: ['Please try again or check the file format'],
            summary: 'Validation error'
          };
          hasErrors = true;
          setFileValidationResults(prev => ({
            ...prev,
            [docId]: results[docId]
          }));
        }

        // Small delay between requests to avoid overwhelming the server
        if (i < filesToValidate.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Calculate final results
      const avgScore = totalFiles > 0 ? Math.round(totalScore / totalFiles) : 0;
      const status = hasErrors ? 'error' : hasWarnings ? 'warning' : 'ok';
      
      // Compile all issues and recommendations
      const allIssues = [];
      const allRecommendations = [];
      const items = [];
      
      Object.entries(results).forEach(([docId, result]) => {
        const docInfo = requiredDocs.find(d => d.id === docId);
        items.push({
          type: result.status === 'ok' ? 'success' : result.status === 'warning' ? 'warning' : 'error',
          title: docInfo?.name || docId,
          description: result.summary || '',
          score: result.score,
          issues: result.issues || [],
          recommendations: result.recommendations || []
        });
        
        if (result.issues) allIssues.push(...result.issues);
        if (result.recommendations) allRecommendations.push(...result.recommendations);
      });

      setValidationResults({
        score: avgScore,
        status,
        summary: `Validated ${totalFiles} document(s). ${hasErrors ? 'Some documents have issues that need attention.' : hasWarnings ? 'Some documents have minor warnings.' : 'All documents look good!'}`,
        items,
        allIssues,
        allRecommendations
      });

    } catch (error) {
      console.error('Validation error:', error);
      setValidationResults({
        score: 0,
        status: 'error',
        summary: 'Failed to validate application',
        items: [{
          type: 'error',
          title: 'Validation Error',
          description: error.message || 'An error occurred while validating. Please try again.',
        }]
      });
    } finally {
      setIsValidating(false);
      setValidationProgress(null);
    }
  };

  // Check if ready to validate
  const canValidate = selectedType && Object.keys(uploadedFiles).length > 0;

  return (
    <Container>
      <Header>
        <Title>Application Review</Title>
        <Subtitle>AI-powered pre-submission validation to catch errors before they cause rejections</Subtitle>
      </Header>

      <MainContent>
        <LeftSection>
          {/* Application Type Selection */}
          <Section>
            <SectionTitle>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Select Application Type
            </SectionTitle>
            
            {/* Category Filter */}
            <CategoryFilter>
              {categories.map(cat => (
                <CategoryButton
                  key={cat}
                  $active={selectedCategory === cat}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setSelectedType(null);
                    setUploadedFiles({});
                    setValidationResults(null);
                  }}
                >
                  {cat}
                </CategoryButton>
              ))}
            </CategoryFilter>
            
            {/* Application Type Dropdown */}
            <ApplicationTypeSelect
              value={selectedType || ''}
              onChange={(e) => {
                setSelectedType(e.target.value || null);
                setUploadedFiles({});
                setValidationResults(null);
              }}
            >
              <option value="">-- Select Application Type --</option>
              {selectedCategory === 'All' ? (
                // Group by category when showing all
                categories.filter(c => c !== 'All').map(category => (
                  <optgroup key={category} label={category}>
                    {APPLICATION_TYPES.filter(t => t.category === category).map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </optgroup>
                ))
              ) : (
                // Show filtered types without grouping
                filteredTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))
              )}
            </ApplicationTypeSelect>
            
            {/* Show selected type info */}
            {selectedType && (
              <SelectedTypeInfo>
                <h4>{APPLICATION_TYPES.find(t => t.id === selectedType)?.name}</h4>
                <p>{APPLICATION_TYPES.find(t => t.id === selectedType)?.description}</p>
              </SelectedTypeInfo>
            )}
          </Section>

          {/* Document Checklist */}
          {selectedType && (
            <Section>
              <SectionTitle>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
                Required Documents
              </SectionTitle>
              <ChecklistContainer>
                {requiredDocs.map(doc => (
                  <ChecklistItem key={doc.id} $uploaded={!!uploadedFiles[doc.id]}>
                    <Checkbox $checked={!!uploadedFiles[doc.id]} $status={fileValidationResults[doc.id]?.status}>
                      {fileValidationResults[doc.id] ? (
                        fileValidationResults[doc.id].status === 'ok' ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        ) : fileValidationResults[doc.id].status === 'warning' ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="3">
                            <path d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4.99c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="3">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="15" y1="9" x2="9" y2="15"/>
                            <line x1="9" y1="9" x2="15" y2="15"/>
                          </svg>
                        )
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </Checkbox>
                    <ChecklistItemContent>
                      <h5>{doc.name}</h5>
                      <span>{doc.description}</span>
                      {fileValidationResults[doc.id] && (
                        <div style={{ 
                          marginTop: '6px', 
                          fontSize: '11px', 
                          color: fileValidationResults[doc.id].status === 'ok' ? '#10b981' : 
                                 fileValidationResults[doc.id].status === 'warning' ? '#f59e0b' : '#ef4444'
                        }}>
                          Score: {fileValidationResults[doc.id].score}/100
                          {fileValidationResults[doc.id].issues?.length > 0 && (
                            <span> • {fileValidationResults[doc.id].issues.length} issue(s)</span>
                          )}
                        </div>
                      )}
                    </ChecklistItemContent>
                    {uploadedFiles[doc.id] ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileName>
                          {uploadedFiles[doc.id].isExisting && '📁 '}
                          {uploadedFiles[doc.id].name}
                        </FileName>
                        <button
                          onClick={() => setUploadedFiles(prev => {
                            const newFiles = { ...prev };
                            delete newFiles[doc.id];
                            return newFiles;
                          })}
                          style={{
                            background: 'rgba(239, 68, 68, 0.2)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            color: '#ef4444',
                            fontSize: '10px'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '6px', position: 'relative' }}>
                        <input
                          type="file"
                          ref={el => fileInputRefs.current[doc.id] = el}
                          style={{ display: 'none' }}
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileUpload(doc.id, e)}
                        />
                        <UploadButton onClick={() => fileInputRefs.current[doc.id]?.click()}>
                          Upload
                        </UploadButton>
                        {existingFiles.length > 0 && (
                          <>
                            <UploadButton 
                              onClick={() => setShowFileSelector(showFileSelector === doc.id ? null : doc.id)}
                              style={{ background: 'rgba(99, 102, 241, 0.2)', borderColor: 'rgba(99, 102, 241, 0.4)' }}
                            >
                              Select
                            </UploadButton>
                            {showFileSelector === doc.id && (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                marginTop: '4px',
                                background: 'rgba(17, 24, 39, 0.98)',
                                border: '1px solid rgba(99, 102, 241, 0.3)',
                                borderRadius: '8px',
                                padding: '8px',
                                minWidth: '250px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                zIndex: 100,
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                              }}>
                                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px', padding: '4px' }}>
                                  Select from existing files:
                                </div>
                                {existingFiles.map((file, idx) => (
                                  <div
                                    key={idx}
                                    onClick={() => handleSelectExistingFile(doc.id, file)}
                                    style={{
                                      padding: '8px 10px',
                                      cursor: 'pointer',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      color: '#e8eaed',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      transition: 'background 0.2s'
                                    }}
                                    onMouseEnter={e => e.target.style.background = 'rgba(99, 102, 241, 0.2)'}
                                    onMouseLeave={e => e.target.style.background = 'transparent'}
                                  >
                                    <span style={{ fontSize: '14px' }}>📄</span>
                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {file.fileName || file.name}
                                    </span>
                                  </div>
                                ))}
                                {existingFiles.length === 0 && (
                                  <div style={{ padding: '12px', color: '#6b7280', fontSize: '12px', textAlign: 'center' }}>
                                    No files found
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </ChecklistItem>
                ))}
              </ChecklistContainer>
            </Section>
          )}

          {/* Application Details */}
          {selectedType && (
            <Section>
              <SectionTitle>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Application Details
              </SectionTitle>
              
              <FormGroup>
                <label>Applicant Name</label>
                <Input
                  type="text"
                  placeholder="Full name as appears on passport"
                  value={applicationDetails.applicantName}
                  onChange={e => setApplicationDetails(prev => ({ ...prev, applicantName: e.target.value }))}
                />
              </FormGroup>
              
              <FormGroup>
                <label>Target Country</label>
                <Input
                  type="text"
                  placeholder="Country applying to"
                  value={applicationDetails.targetCountry}
                  onChange={e => setApplicationDetails(prev => ({ ...prev, targetCountry: e.target.value }))}
                />
              </FormGroup>
              
              <FormGroup>
                <label>Program / Position Details</label>
                <TextArea
                  placeholder="Describe the job, program, or purpose of the application..."
                  value={applicationDetails.programDetails}
                  onChange={e => setApplicationDetails(prev => ({ ...prev, programDetails: e.target.value }))}
                />
              </FormGroup>
              
              <FormGroup>
                <label>Additional Notes</label>
                <TextArea
                  placeholder="Any special circumstances or notes for validation..."
                  value={applicationDetails.additionalNotes}
                  onChange={e => setApplicationDetails(prev => ({ ...prev, additionalNotes: e.target.value }))}
                />
              </FormGroup>

              <ValidateButton onClick={handleValidate} disabled={!canValidate || isValidating}>
                {isValidating ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12a9 9 0 11-6.219-8.56"/>
                    </svg>
                    Validating...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    Validate Application
                  </>
                )}
              </ValidateButton>
            </Section>
          )}
          
          {/* Form Completion Status Section */}
          {selectedType && (
            <Section>
              <SectionTitle>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                </svg>
                Form Completion Status
              </SectionTitle>
              
              {loadingFormStatus ? (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: 'rgba(180, 195, 220, 0.6)' 
                }}>
                  Loading form status...
                </div>
              ) : formCompletionData?.forms?.length > 0 ? (
                <>
                  {/* Summary */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      padding: '12px',
                      background: 'rgba(16, 185, 129, 0.1)',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '20px', fontWeight: '600', color: '#10b981' }}>
                        {formCompletionData.summary.completedForms}
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(180, 195, 220, 0.6)' }}>
                        Complete
                      </div>
                    </div>
                    <div style={{
                      padding: '12px',
                      background: 'rgba(245, 158, 11, 0.1)',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '20px', fontWeight: '600', color: '#f59e0b' }}>
                        {formCompletionData.summary.incompleteForms}
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(180, 195, 220, 0.6)' }}>
                        Incomplete
                      </div>
                    </div>
                    <div style={{
                      padding: '12px',
                      background: 'rgba(99, 102, 241, 0.1)',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '20px', fontWeight: '600', color: '#6366f1' }}>
                        {formCompletionData.summary.overallCompletionPercent}%
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(180, 195, 220, 0.6)' }}>
                        Overall
                      </div>
                    </div>
                  </div>
                  
                  {/* Forms list */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {formCompletionData.forms.map((form, idx) => (
                      <div 
                        key={idx}
                        style={{
                          padding: '12px 16px',
                          background: 'rgba(30, 41, 59, 0.5)',
                          borderRadius: '8px',
                          border: `1px solid ${form.isComplete ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          marginBottom: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ 
                              fontSize: '16px',
                              color: form.isComplete ? '#10b981' : '#f59e0b'
                            }}>
                              {form.isComplete ? '✓' : '⚠'}
                            </span>
                            <span style={{ 
                              color: '#e8eaed', 
                              fontWeight: '500',
                              fontSize: '13px'
                            }}>
                              {form.formName}
                            </span>
                          </div>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            background: form.isComplete ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                            color: form.isComplete ? '#10b981' : '#f59e0b'
                          }}>
                            {form.completionPercent}%
                          </span>
                        </div>
                        
                        {/* Progress bar */}
                        <div style={{
                          height: '4px',
                          background: 'rgba(75, 85, 99, 0.3)',
                          borderRadius: '2px',
                          overflow: 'hidden',
                          marginBottom: '8px'
                        }}>
                          <div style={{
                            width: `${form.completionPercent}%`,
                            height: '100%',
                            background: form.isComplete ? '#10b981' : '#f59e0b',
                            borderRadius: '2px',
                            transition: 'width 0.3s'
                          }} />
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          gap: '16px',
                          fontSize: '11px',
                          color: 'rgba(180, 195, 220, 0.6)'
                        }}>
                          <span>Filled: {form.prefilledCount + form.collectedCount}/{form.totalFields}</span>
                          {form.remainingCount > 0 && (
                            <span style={{ color: '#f59e0b' }}>
                              Missing: {form.remainingCount} fields
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ 
                  padding: '24px', 
                  textAlign: 'center',
                  background: 'rgba(30, 41, 59, 0.3)',
                  borderRadius: '8px'
                }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>📋</div>
                  <div style={{ color: 'rgba(180, 195, 220, 0.6)', fontSize: '13px' }}>
                    No forms have been assigned to this case yet.
                  </div>
                  <div style={{ color: 'rgba(180, 195, 220, 0.4)', fontSize: '11px', marginTop: '4px' }}>
                    Assign forms from the Forms panel to see completion status here.
                  </div>
                </div>
              )}
            </Section>
          )}
        </LeftSection>

        <RightSection>
          {isValidating ? (
            <LoadingOverlay>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 11-6.219-8.56"/>
              </svg>
              <h4>Analyzing Documents</h4>
              {validationProgress ? (
                <>
                  <p style={{ marginBottom: '16px' }}>
                    Validating document {validationProgress.current} of {validationProgress.total}
                  </p>
                  <div style={{
                    width: '100%',
                    maxWidth: '300px',
                    background: 'rgba(75, 85, 99, 0.3)',
                    borderRadius: '8px',
                    height: '8px',
                    overflow: 'hidden',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: `${(validationProgress.current / validationProgress.total) * 100}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
                      borderRadius: '8px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'rgba(180, 195, 220, 0.7)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span style={{ fontWeight: '500', color: '#a5b4fc' }}>{validationProgress.currentDoc}</span>
                    <span>{validationProgress.currentFile}</span>
                  </div>
                  {/* Show completed files */}
                  {Object.keys(fileValidationResults).length > 0 && (
                    <div style={{ 
                      marginTop: '20px',
                      padding: '12px',
                      background: 'rgba(16, 185, 129, 0.1)',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}>
                      <div style={{ color: '#10b981', marginBottom: '8px' }}>
                        ✓ {Object.keys(fileValidationResults).length} document(s) validated
                      </div>
                      {Object.entries(fileValidationResults).map(([docId, result]) => (
                        <div key={docId} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          color: result.status === 'ok' ? '#10b981' : result.status === 'warning' ? '#f59e0b' : '#ef4444',
                          marginTop: '4px'
                        }}>
                          <span>{result.status === 'ok' ? '✓' : result.status === 'warning' ? '⚠' : '✗'}</span>
                          <span>{requiredDocs.find(d => d.id === docId)?.name || docId}</span>
                          <span style={{ marginLeft: 'auto', opacity: 0.7 }}>{result.score}/100</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p>Preparing validation...</p>
              )}
            </LoadingOverlay>
          ) : validationResults ? (
            <ResultsContainer>
              <ResultsHeader>
                <ScoreCircle $score={validationResults.score}>
                  <span>{validationResults.score}</span>
                  <span>/ 100</span>
                </ScoreCircle>
                <ResultsSummary $score={validationResults.score}>
                  <h3>
                    {validationResults.score >= 80 ? 'Ready to Submit' :
                     validationResults.score >= 60 ? 'Needs Attention' :
                     'Not Ready'}
                  </h3>
                  <p>{validationResults.summary}</p>
                </ResultsSummary>
              </ResultsHeader>

              {/* Errors */}
              {validationResults.items?.filter(i => i.type === 'error').length > 0 && (
                <ResultsSection>
                  <h4>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M15 9l-6 6M9 9l6 6"/>
                    </svg>
                    Issues to Fix ({validationResults.items.filter(i => i.type === 'error').length})
                  </h4>
                  {validationResults.items.filter(i => i.type === 'error').map((item, idx) => (
                    <ResultItem key={idx} $type="error">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M15 9l-6 6M9 9l6 6"/>
                      </svg>
                      <ResultItemContent>
                        <h5>{item.title}</h5>
                        <p>{item.description}</p>
                      </ResultItemContent>
                    </ResultItem>
                  ))}
                </ResultsSection>
              )}

              {/* Warnings */}
              {validationResults.items?.filter(i => i.type === 'warning').length > 0 && (
                <ResultsSection>
                  <h4>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                      <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                    </svg>
                    Warnings ({validationResults.items.filter(i => i.type === 'warning').length})
                  </h4>
                  {validationResults.items.filter(i => i.type === 'warning').map((item, idx) => (
                    <ResultItem key={idx} $type="warning">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                      </svg>
                      <ResultItemContent>
                        <h5>{item.title}</h5>
                        <p>{item.description}</p>
                      </ResultItemContent>
                    </ResultItem>
                  ))}
                </ResultsSection>
              )}

              {/* Successes */}
              {validationResults.items?.filter(i => i.type === 'success').length > 0 && (
                <ResultsSection>
                  <h4>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    Passed ({validationResults.items.filter(i => i.type === 'success').length})
                  </h4>
                  {validationResults.items.filter(i => i.type === 'success').map((item, idx) => (
                    <ResultItem key={idx} $type="success">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <ResultItemContent>
                        <h5>{item.title}</h5>
                        <p>{item.description}</p>
                      </ResultItemContent>
                    </ResultItem>
                  ))}
                </ResultsSection>
              )}
            </ResultsContainer>
          ) : (
            <EmptyResults>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
              </svg>
              <h4>Validation Results</h4>
              <p>Select an application type, upload your documents, and click validate to see AI-powered analysis.</p>
            </EmptyResults>
          )}
        </RightSection>
      </MainContent>
    </Container>
  );
};

export default ApplicationReview;

